import type { TFunction } from 'i18next';
import {
  isCriteriaDateColumn,
  isDateSingleValueOperator,
  isIntervalDateOperator,
  isValidYyyyMmDd,
  parseIntervalValue,
  serializeIntervalValue,
} from '../components/tables/commons/search/u2000DateCriteriaUtils';
import { normalizeBooleanCriteriaValue } from '../components/tables/commons/search/U2000SearchBooleanValueCombo';
import type { Criteria, U2000SearchStateFilter } from '../reducer/formReducer/U2000SearchState';
import { u2000ShiftCivilYmd } from './U2000DateTimeHelpers';

/** Token dynamique persisté — « today », « today-7 », « today+3 ». */
export type U2000DynamicDateToken = string;

export type StoredDateScalar =
  | string
  | { kind: 'fixed'; value: string }
  | { kind: 'dynamic'; token: U2000DynamicDateToken };

export type StoredIntervalValue =
  | string
  | { start?: StoredDateScalar; end?: StoredDateScalar };

export interface CriteriaDynamicDate {
  single?: U2000DynamicDateToken;
  intervalStart?: U2000DynamicDateToken;
  intervalEnd?: U2000DynamicDateToken;
}

export interface U2000SnapshotDateOptions {
  makeDatesDynamic?: boolean;
  referenceTodayYmd: string;
}

/** Écart en jours civils entre deux dates yyyy-MM-dd (toYmd − fromYmd). */
export function u2000CivilDayDiff(fromYmd: string, toYmd: string): number | null {
  if (!isValidYyyyMmDd(fromYmd) || !isValidYyyyMmDd(toYmd)) {
    return null;
  }
  const parse = (ymd: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
    if (!m) return null;
    return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  };
  const fromMs = parse(fromYmd);
  const toMs = parse(toYmd);
  if (fromMs == null || toMs == null) {
    return null;
  }
  return Math.round((toMs - fromMs) / (24 * 60 * 60 * 1000));
}

/** Convertit une date fixe en token relatif à todayYmd. */
export function u2000YmdToDynamicToken(ymd: string, todayYmd: string): U2000DynamicDateToken | null {
  if (!isValidYyyyMmDd(ymd) || !isValidYyyyMmDd(todayYmd)) {
    return null;
  }
  const diff = u2000CivilDayDiff(todayYmd, ymd);
  if (diff == null) {
    return null;
  }
  if (diff === 0) {
    return 'today';
  }
  if (diff < 0) {
    return `today${diff}`;
  }
  return `today+${diff}`;
}

/** Résout un token dynamique vers yyyy-MM-dd (fuseau org via todayYmd fourni). */
export function u2000ResolveDynamicToken(token: U2000DynamicDateToken, todayYmd: string): string | null {
  if (!isValidYyyyMmDd(todayYmd)) {
    return null;
  }
  if (token === 'today') {
    return todayYmd;
  }
  const minusMatch = /^today-(\d+)$/.exec(token);
  if (minusMatch) {
    return u2000ShiftCivilYmd(todayYmd, -Number(minusMatch[1]));
  }
  const plusMatch = /^today\+(\d+)$/.exec(token);
  if (plusMatch) {
    return u2000ShiftCivilYmd(todayYmd, Number(plusMatch[1]));
  }
  return null;
}

function isStoredDateScalar(value: unknown): value is StoredDateScalar {
  if (typeof value === 'string') {
    return true;
  }
  if (value && typeof value === 'object' && 'kind' in value) {
    const kind = (value as { kind?: string }).kind;
    return kind === 'fixed' || kind === 'dynamic';
  }
  return false;
}

function isStoredIntervalValue(value: unknown): value is StoredIntervalValue {
  if (typeof value === 'string') {
    return true;
  }
  if (value && typeof value === 'object' && ('start' in value || 'end' in value)) {
    const obj = value as { start?: unknown; end?: unknown };
    return (
      (obj.start == null || isStoredDateScalar(obj.start)) &&
      (obj.end == null || isStoredDateScalar(obj.end))
    );
  }
  return false;
}

function parseStoredScalar(
  scalar: StoredDateScalar | undefined,
  todayYmd: string,
): { ymd: string | null; token?: U2000DynamicDateToken } {
  if (scalar == null) {
    return { ymd: null };
  }
  if (typeof scalar === 'string') {
    return { ymd: isValidYyyyMmDd(scalar) ? scalar : null };
  }
  if (scalar.kind === 'fixed') {
    return { ymd: isValidYyyyMmDd(scalar.value) ? scalar.value : null };
  }
  const ymd = u2000ResolveDynamicToken(scalar.token, todayYmd);
  return { ymd, token: scalar.token };
}

function toFixedStoredScalar(ymd: string): StoredDateScalar {
  return { kind: 'fixed', value: ymd };
}

function toDynamicStoredScalar(ymd: string, todayYmd: string): StoredDateScalar | null {
  const token = u2000YmdToDynamicToken(ymd, todayYmd);
  if (!token) {
    return null;
  }
  return { kind: 'dynamic', token };
}

/** Critère date avec valeur persistable (simple ou intervalle) — exclut isNull / isNotNull. */
export function u2000CriteriaHasPersistableDateValue(criteria: Criteria): boolean {
  if (!isCriteriaDateColumn(criteria.field)) {
    return false;
  }
  const op = criteria.condition?.name;
  if (!op || op.toLowerCase() === 'isnull' || op.toLowerCase() === 'isnotnull') {
    return false;
  }
  const raw = String(criteria.value ?? '').trim();
  if (!raw) {
    return false;
  }
  if (isIntervalDateOperator(op)) {
    const range = parseIntervalValue(raw);
    return Boolean(range.start || range.end);
  }
  return isDateSingleValueOperator(op) && isValidYyyyMmDd(raw);
}

export function u2000FilterHasDateCriteria(filter: U2000SearchStateFilter): boolean {
  return filter.criterias.some((c) => u2000CriteriaHasPersistableDateValue(c));
}

/** Libellé i18n pour un token dynamique (chips / critères actifs). */
export function u2000FormatDynamicDateTokenLabel(token: U2000DynamicDateToken, t: TFunction): string {
  if (token === 'today') {
    return t('lblDynamicDateToday');
  }
  const minusMatch = /^today-(\d+)$/.exec(token);
  if (minusMatch) {
    return t('lblDynamicDateTodayMinus', { count: Number(minusMatch[1]) });
  }
  const plusMatch = /^today\+(\d+)$/.exec(token);
  if (plusMatch) {
    return t('lblDynamicDateTodayPlus', { count: Number(plusMatch[1]) });
  }
  return token;
}

function formatIntervalDynamicLabel(
  startToken: U2000DynamicDateToken | undefined,
  endToken: U2000DynamicDateToken | undefined,
  resolvedStart: string | undefined,
  resolvedEnd: string | undefined,
  t: TFunction,
): string | null {
  const startLabel = startToken
    ? u2000FormatDynamicDateTokenLabel(startToken, t)
    : resolvedStart ?? '';
  const endLabel = endToken ? u2000FormatDynamicDateTokenLabel(endToken, t) : resolvedEnd ?? '';
  if (startToken || endToken) {
    if (startLabel && endLabel) {
      return `${startLabel} — ${endLabel}`;
    }
    return startLabel || endLabel || null;
  }
  return null;
}

/** Libellé chip pour un critère date avec tokens dynamiques. */
export function u2000FormatCriteriaDynamicDateDisplay(
  criteria: Pick<Criteria, 'value' | 'dynamicDate' | 'condition'>,
  t: TFunction,
): string | null {
  const dynamicDate = criteria.dynamicDate;
  if (!dynamicDate) {
    return null;
  }
  if (dynamicDate.single) {
    return u2000FormatDynamicDateTokenLabel(dynamicDate.single, t);
  }
  if (dynamicDate.intervalStart || dynamicDate.intervalEnd) {
    const range = parseIntervalValue(String(criteria.value ?? ''));
    return formatIntervalDynamicLabel(
      dynamicDate.intervalStart,
      dynamicDate.intervalEnd,
      range.start,
      range.end,
      t,
    );
  }
  return null;
}

/** Applique le choix fixe/dynamique aux critères en mémoire (value string + dynamicDate). */
export function u2000ApplyDynamicDateSaveToCriterias(
  criterias: Criteria[],
  makeDynamic: boolean,
  todayYmd: string,
): Criteria[] {
  return criterias.map((c) =>
    u2000DeserializeStoredCriteriaDates(
      u2000SerializeCriteriaDateForStorage(c, makeDynamic, todayYmd),
      todayYmd,
    ),
  );
}
/** Sérialise value (+ dynamicDate) d'un critère date pour localStorage. */
export function u2000SerializeCriteriaDateForStorage(
  criteria: Criteria,
  makeDynamic: boolean,
  todayYmd: string,
): Criteria {
  if (!u2000CriteriaHasPersistableDateValue(criteria)) {
    const { dynamicDate: _dynamicDate, ...rest } = criteria;
    return rest;
  }

  const op = criteria.condition.name;
  const raw = String(criteria.value ?? '').trim();

  if (isIntervalDateOperator(op)) {
    const range = parseIntervalValue(raw);
    if (makeDynamic) {
      const intervalStart = range.start ? u2000YmdToDynamicToken(range.start, todayYmd) ?? undefined : undefined;
      const intervalEnd = range.end ? u2000YmdToDynamicToken(range.end, todayYmd) ?? undefined : undefined;
      const stored: StoredIntervalValue = {
        start: range.start ? toDynamicStoredScalar(range.start, todayYmd) ?? toFixedStoredScalar(range.start) : undefined,
        end: range.end ? toDynamicStoredScalar(range.end, todayYmd) ?? toFixedStoredScalar(range.end) : undefined,
      };
      return {
        ...criteria,
        value: stored as unknown as string,
        dynamicDate: { intervalStart, intervalEnd },
      };
    }
    return {
      ...criteria,
      value: serializeIntervalValue(range),
      dynamicDate: undefined,
    };
  }

  if (makeDynamic) {
    const token = u2000YmdToDynamicToken(raw, todayYmd);
    if (token) {
      return {
        ...criteria,
        value: { kind: 'dynamic', token } as unknown as string,
        dynamicDate: { single: token },
      };
    }
  }

  return {
    ...criteria,
    value: raw,
    dynamicDate: undefined,
  };
}

/** Lit un critère stocké (legacy ou typé) → value résolue + dynamicDate. */
export function u2000DeserializeStoredCriteriaDates(criteria: Criteria, todayYmd: string): Criteria {
  if (!isCriteriaDateColumn(criteria.field)) {
    return criteria;
  }

  const op = criteria.condition?.name;
  const storedValue = criteria.value as unknown;

  if (isIntervalDateOperator(op) && isStoredIntervalValue(storedValue)) {
    if (typeof storedValue === 'string') {
      const range = parseIntervalValue(storedValue);
      return {
        ...criteria,
        value: serializeIntervalValue(range),
        dynamicDate: criteria.dynamicDate,
      };
    }
    const startParsed = parseStoredScalar(storedValue.start, todayYmd);
    const endParsed = parseStoredScalar(storedValue.end, todayYmd);
    const range = {
      start: startParsed.ymd ?? undefined,
      end: endParsed.ymd ?? undefined,
    };
    const dynamicDate: CriteriaDynamicDate | undefined =
      startParsed.token || endParsed.token
        ? {
            intervalStart: startParsed.token,
            intervalEnd: endParsed.token,
          }
        : undefined;
    return {
      ...criteria,
      value: serializeIntervalValue(range),
      dynamicDate,
    };
  }

  if (isDateSingleValueOperator(op) && isStoredDateScalar(storedValue)) {
    if (typeof storedValue === 'string') {
      return { ...criteria, value: storedValue, dynamicDate: criteria.dynamicDate };
    }
    const parsed = parseStoredScalar(storedValue, todayYmd);
    return {
      ...criteria,
      value: parsed.ymd ?? String(criteria.value ?? ''),
      dynamicDate: parsed.token ? { single: parsed.token } : undefined,
    };
  }

  return criteria;
}

/** Ré-applique les tokens dynamiques avant envoi API search. */
export function u2000ResolveCriteriaDynamicDates(criteria: Criteria, todayYmd: string): Criteria {
  const dynamicDate = criteria.dynamicDate;
  if (!dynamicDate) {
    return criteria;
  }

  const op = criteria.condition?.name;

  if (dynamicDate.single && isDateSingleValueOperator(op)) {
    const resolved = u2000ResolveDynamicToken(dynamicDate.single, todayYmd);
    if (resolved) {
      return { ...criteria, value: resolved };
    }
    return criteria;
  }

  if ((dynamicDate.intervalStart || dynamicDate.intervalEnd) && isIntervalDateOperator(op)) {
    const current = parseIntervalValue(String(criteria.value ?? ''));
    const start =
      dynamicDate.intervalStart != null
        ? u2000ResolveDynamicToken(dynamicDate.intervalStart, todayYmd) ?? current.start
        : current.start;
    const end =
      dynamicDate.intervalEnd != null
        ? u2000ResolveDynamicToken(dynamicDate.intervalEnd, todayYmd) ?? current.end
        : current.end;
    return {
      ...criteria,
      value: serializeIntervalValue({ start, end }),
    };
  }

  return criteria;
}

/** Hydrate tous les critères date d'un filtre lu depuis localStorage. */
export function u2000HydrateStoredFilterDates(filter: U2000SearchStateFilter, todayYmd: string): U2000SearchStateFilter {
  return {
    ...filter,
    criterias: filter.criterias.map((c) => u2000DeserializeStoredCriteriaDates(c, todayYmd)),
  };
}

function normalizeFilterFieldNames(fields: U2000SearchStateFilter['fields']): string[] {
  return (fields ?? [])
    .map((f) => {
      const col = (f as { field?: { name?: string }; name?: string }).field ?? f;
      if (typeof col === 'string') {
        return col;
      }
      return col?.name ?? '';
    })
    .filter((name) => name.length > 0)
    .sort();
}

function normalizeFilterSortedFields(
  sortedFields: U2000SearchStateFilter['sortedFields'],
): Array<{ name: string; sortOrder: string }> {
  return (sortedFields ?? [])
    .map((s) => {
      const item = s as { field?: { name?: string }; name?: string; sortOrder?: string };
      const col = item.field ?? s;
      const name = typeof col === 'string' ? col : col?.name ?? item.name ?? '';
      return { name, sortOrder: item.sortOrder ?? 'ASC' };
    })
    .filter((s) => s.name.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));
}

function normalizeCriteriaComparableValue(c: Criteria): unknown {
  if (c.field?.type === 'boolean') {
    return normalizeBooleanCriteriaValue(c.value) ?? String(c.value ?? '');
  }
  return c.value;
}

function normalizeFilterCriteria(
  criterias: Criteria[],
): Array<Record<string, unknown>> {
  return criterias
    .map((c) => {
      const base: Record<string, unknown> = {
        field: c.field?.name,
        condition: c.condition?.name?.toLowerCase(),
      };
      if (c.dynamicDate) {
        base.dynamicDate = c.dynamicDate;
        return base;
      }
      base.value = normalizeCriteriaComparableValue(c);
      return base;
    })
    .sort((a, b) => String(a.field).localeCompare(String(b.field)));
}

/** Compare deux filtres (courant vs stocké) en normalisant les dates dynamiques. */
export function u2000SearchFiltersEquivalent(
  a: U2000SearchStateFilter,
  b: U2000SearchStateFilter,
  todayYmd: string,
): boolean {
  const normalize = (filter: U2000SearchStateFilter) => {
    const hydrated = u2000HydrateStoredFilterDates(filter, todayYmd);
    return {
      fields: normalizeFilterFieldNames(hydrated.fields),
      sortedFields: normalizeFilterSortedFields(hydrated.sortedFields),
      criterias: normalizeFilterCriteria(hydrated.criterias),
    };
  };
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
}
